import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {Document, Page, pdfjs} from "react-pdf";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {anchorScroll, beginPinch, MIN_SCALE, pinchTransform} from "../utils/documentZoom";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

export default function DocumentViewer(props) {
    const {
        uri,
        pdf,
        renderFooter,
    } = props;

    const documentWrapperRef = useRef(null);

    const documentDefaultStyle = {
        objectFit: 'contain',
        height: '0',
        width: '0',
    };

    const [documentStyle, setDocumentStyle] = useState(documentDefaultStyle);

    const [dimensions, setDimensions] = React.useState({
        height: window.innerHeight,
        width: window.innerWidth
    });

    useEffect(() => {
        function handleResize() {
            setDimensions({
                height: window.innerHeight,
                width: window.innerWidth
            });

            setDocumentStyle(documentDefaultStyle);

            if (!documentWrapperRef.current) {
                return;
            }

            const newDocumentStyle = {
                height: documentWrapperRef.current.clientHeight,
                width: documentWrapperRef.current.clientWidth,
            };

            setDocumentStyle(newDocumentStyle);
        }

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        }
        // documentDefaultStyle is an object literal rebuilt on every render;
        // listing it would remove and re-add the resize listener every time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentWrapperRef]);

    const [numPages, setNumPages] = useState(null);

    const [renderSuccess, setRenderSuccess] = useState(false);
    const [renderIncrement, setRenderIncrement] = useState(0);

    function onDocumentLoadSuccess({numPages}) {
        setRenderSuccess(true);
        setNumPages(numPages);
    }

    useEffect(() => {
        if (renderSuccess) {
            return;
        }

        const retry = setTimeout(() => setRenderIncrement((increment) => increment + 1), 1000);

        return () => clearTimeout(retry);
    }, [renderSuccess, renderIncrement]);

    const [pages, setPages] = useState([]);
    useEffect(() => {
        const tmpPages = [];
        for (var i = 0; i < numPages; i++) {
            tmpPages.push(<Page key={'page_' + i + 1} pageNumber={i+1} width={documentStyle.width} height={documentStyle.height}/>);
        }
        setPages(tmpPages);

        // Deliberately keyed on page count alone. documentStyle.width/height
        // change on every resize, and rebuilding the Page list re-renders each
        // PDF canvas - the same cost the zoom path avoids by transforming
        // instead of redrawing (see the comment below). The trade-off is that
        // pages keep the dimensions they were first laid out with.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numPages]);

    // Zooming transforms the pages rather than re-rendering them: a packet
    // redrawn at 4x would blow past the iPad's per-canvas pixel limit.
    const scrollRef = useRef(null);
    const [contentNode, setContentNode] = useState(null);

    const [scale, setScale] = useState(MIN_SCALE);
    const scaleRef = useRef(MIN_SCALE);
    const [gesture, setGesture] = useState(null);
    const pinchRef = useRef(null);
    const pendingScrollRef = useRef(null);

    const [contentSize, setContentSize] = useState({width: 0, height: 0});
    const contentSizeRef = useRef(contentSize);
    contentSizeRef.current = contentSize;

    // A pinch lives only while exactly two fingers are down. Anything else — a
    // third finger, or one lifting — commits what it reached and lets go, since
    // the anchor is resolved against scroll offsets that native scrolling would
    // invalidate the moment the gesture stops swallowing touches.
    const commitPinch = useCallback(() => {
        const pinch = pinchRef.current;
        if (!pinch) {
            return;
        }

        pinchRef.current = null;
        setGesture(null);

        if (pinch.scale !== scaleRef.current) {
            pendingScrollRef.current = anchorScroll(pinch, pinch.scale);
            setScale(pinch.scale);
        }
    }, []);

    const onTouchStart = useCallback((event) => {
        const container = scrollRef.current;
        if (!container) {
            return;
        }

        if (event.touches.length !== 2) {
            commitPinch();
            return;
        }

        event.preventDefault();
        pinchRef.current = beginPinch({
            touchA: event.touches[0],
            touchB: event.touches[1],
            containerRect: container.getBoundingClientRect(),
            scrollLeft: container.scrollLeft,
            scrollTop: container.scrollTop,
            scale: scaleRef.current,
            viewport: {width: container.clientWidth, height: container.clientHeight},
            content: contentSizeRef.current,
        });
        setGesture({scale: scaleRef.current, tx: 0, ty: 0});
    }, [commitPinch]);

    const onTouchMove = useCallback((event) => {
        const pinch = pinchRef.current;
        if (!pinch) {
            return;
        }

        if (event.touches.length !== 2) {
            commitPinch();
            return;
        }

        event.preventDefault();

        const next = pinchTransform(pinch, event.touches[0], event.touches[1]);
        pinch.scale = next.scale;
        pinch.midX = next.midX;
        pinch.midY = next.midY;
        setGesture(next);
    }, [commitPinch]);

    // Native listeners: React registers touch handlers passively, so
    // preventDefault() from an onTouchMove prop would be ignored.
    const attachScrollRef = useCallback((node) => {
        const previous = scrollRef.current;
        if (previous) {
            previous.removeEventListener('touchstart', onTouchStart);
            previous.removeEventListener('touchmove', onTouchMove);
            previous.removeEventListener('touchend', commitPinch);
            previous.removeEventListener('touchcancel', commitPinch);
        }

        scrollRef.current = node;

        if (node) {
            node.addEventListener('touchstart', onTouchStart, {passive: false});
            node.addEventListener('touchmove', onTouchMove, {passive: false});
            node.addEventListener('touchend', commitPinch);
            node.addEventListener('touchcancel', commitPinch);
        }
    }, [onTouchStart, onTouchMove, commitPinch]);

    useLayoutEffect(() => {
        scaleRef.current = scale;

        const container = scrollRef.current;
        const pendingScroll = pendingScrollRef.current;
        pendingScrollRef.current = null;

        if (container && pendingScroll) {
            container.scrollLeft = pendingScroll.left;
            container.scrollTop = pendingScroll.top;
        }
    }, [scale]);

    // Through state rather than a ref: the wrapper below remounts on resize,
    // which would strand the observer on a detached node.
    useEffect(() => {
        if (!contentNode) {
            return;
        }

        const measure = () => setContentSize({
            width: contentNode.offsetWidth,
            height: contentNode.offsetHeight,
        });
        measure();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(contentNode);

        return () => observer.disconnect();
    }, [contentNode, pages]);

    // The next customer must not inherit the previous one's zoom.
    useEffect(() => {
        pinchRef.current = null;
        pendingScrollRef.current = null;
        setGesture(null);
        setScale(MIN_SCALE);

        if (scrollRef.current) {
            scrollRef.current.scrollLeft = 0;
            scrollRef.current.scrollTop = 0;
        }
    }, [pdf, uri]);

    const zoomed = gesture !== null || scale !== MIN_SCALE;
    const transform = gesture
        ? `translate(${gesture.tx}px, ${gesture.ty}px) scale(${gesture.scale})`
        : `scale(${scale})`;

    const document = () => {
        // if dimensions change, the KEY will force a rerender on the component and the zoom of the document will be recalculated.
        // Probably will need to convert PDF to IMG as pdf preview does not work on Android Chrome. Not sure about iOS
        return <div ref={documentWrapperRef} key={dimensions.height + '_' + dimensions.width + '_' + renderIncrement + '_' + numPages}
                    style={{
                        width: '100%',
                        height: '100%',
                    }}>
            <div style={{
                width: documentStyle.width,
                height: documentStyle.height,
                display: 'flex',
                flexDirection: 'column',
            }}>
                <div ref={attachScrollRef}
                     style={{
                         flex: 1,
                         minHeight: 0,
                         overflowY: 'scroll',
                         overflowX: zoomed ? 'auto' : 'hidden',
                         WebkitOverflowScrolling: 'touch',
                         touchAction: 'pan-x pan-y',
                     }}>
                    {/* Committed scale only: transforming during the gesture
                        keeps the scroll offsets still under the fingers. */}
                    <div style={contentSize.height > 0
                        ? {width: contentSize.width * scale, height: contentSize.height * scale}
                        : {width: '100%'}}>
                        {/* Height stays auto so this measures the whole document,
                            not one screenful, which is what sizes the pan area. */}
                        <div ref={setContentNode}
                             style={{
                                 width: documentStyle.width,
                                 transformOrigin: '0 0',
                                 transform: zoomed ? transform : undefined,
                                 willChange: gesture ? 'transform' : undefined,
                             }}>
                            <Document
                                file={pdf}
                                onLoadSuccess={(e) => onDocumentLoadSuccess(e)}>
                                {pages}
                            </Document>
                        </div>
                    </div>
                </div>
                {/* Outside the scroller so the actions stay reachable at any zoom. */}
                {renderFooter && <div style={{flexShrink: 0}}>{renderFooter()}</div>}
            </div>
        </div>
    }

    return document();
}
